import axios from "axios";
import { getTenantSlug } from "./tenant";
import { handleRuntimeAccessError } from "./runtimeAccess";
import { installIdempotentRetry, shouldRetryIdempotent, _setRetryDelayMs } from "./httpRetry";

// INFRA-8: the ambient-gateway-5xx retry is ONE rule shared by every transport in this app
// (staff, parent, student, the two marketing pages' private instance, and the raw-fetch tenant
// sites) — see src/lib/httpRetry.js for the exclusions and why each one is load-bearing.
// Re-exported here because this module is the app's canonical HTTP entry point.
export { shouldRetryIdempotent, _setRetryDelayMs };

// Empty in dev (relative `/api`, proxied to :8001 by CRA's package.json `proxy`); an absolute
// URL is baked in at build time for prod. Without the `|| ""` fallback an unset env var stringifies
// to the literal "undefined", producing `/undefined/api/...` 404s (breaks login in dev).
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND}/api`;

// Staff/admin auth is carried by the httpOnly `access_token` cookie set by the backend
// on login. The browser sends it automatically because of withCredentials — no token is
// ever stored in JS (localStorage), so it can't be stolen via XSS.
const api = axios.create({ baseURL: API, withCredentials: true });

// --- Conditional GET (ETag / If-None-Match) for the steady-state polls -----------------------
// Phase-1D transport: the three per-tab polls (/auth/me authority refresh, the notification bell,
// the Inbox unread badge) re-download an unchanged JSON payload every ~60s. The backend stamps an
// ETag on them; this interceptor pair remembers the last ETag + body per URL, revalidates with
// If-None-Match, and maps the server's empty 304 back to the cached body — callers keep reading a
// normal `{ data }` and, because the cached body is the SAME object reference on every unchanged
// poll, React state setters bail out of re-rendering. A 304 is only surfaced to JS when JS itself
// sent the conditional header, so this never fights the browser's own cache.
const CONDITIONAL_GET_PATHS = new Set([
  "/auth/me",
  "/dashboard/notifications",
  "/communication/inbox/unread",
]);
const etagCache = new Map(); // url -> { etag, data }
const isConditionalGet = (config) =>
  (config?.method || "get").toLowerCase() === "get" && CONDITIONAL_GET_PATHS.has(config?.url);
export const _resetEtagCache = () => etagCache.clear(); // test hook

api.interceptors.request.use((config) => {
  const slug = getTenantSlug();
  if (slug) config.headers["X-Tenant-Slug"] = slug;
  if (isConditionalGet(config)) {
    const cached = etagCache.get(config.url);
    if (cached?.etag) {
      config.headers["If-None-Match"] = cached.etag;
      // axios' default validateStatus rejects 304 — accept it here (and only here).
      const prev = config.validateStatus;
      config.validateStatus = (s) => s === 304 || (prev ? prev(s) : s >= 200 && s < 300);
    }
  }
  return config;
});

// EDGE-01 (production, 2026-08-31): CloudFront's distribution-level custom_error_response maps
// 403 -> 200 /200.html and 404 -> 200 /200.html. CustomErrorResponses hangs off DistributionConfig,
// so it rewrites the /api/* behaviour identically to the SPA behaviour: an API permission denial or
// a missing record arrives here as a SUCCESSFUL 200 whose body is the SPA shell.
//
// The damage is not cosmetic. PaymentModal reads `data?.payment?.amount` off that HTML, gets
// undefined, and the cashier is shown a GREEN success toast for a payment that recorded nothing.
// Every `status === 403 || status === 404` branch in this app is unreachable in production for the
// same reason, so entitlement and permission denials never run their handlers.
//
// The infrastructure fix (move the SPA fallback into the viewer-request function so /api/* is
// exempt) is the real one and is tracked separately. This guard is independent of it: an API
// response that claims 2xx while carrying text/html is never a valid success, so convert it into
// the rejection the caller already knows how to handle. Harmless once the edge is fixed.
function isHtmlMasqueradingAsSuccess(response) {
  const type = String(response?.headers?.["content-type"] || "");
  if (!type.includes("text/html")) return false;
  // Only /api responses are ever JSON contracts; never second-guess a deliberate HTML fetch.
  return String(response?.config?.url || "").includes("/api/") ||
    String(response?.config?.baseURL || "").includes("/api");
}

// Registered BEFORE the interceptor below so a transient 502 is ridden out without waking the
// ETag/edge-mask/runtime-access handlers for a request that is about to succeed.
installIdempotentRetry(api);

api.interceptors.response.use(
  (response) => {
    if (isHtmlMasqueradingAsSuccess(response)) {
      const err = new Error("The server returned a page instead of data (the request was denied or the record was not found).");
      err.response = { ...response, status: 0, data: { detail: "edge_rewrote_error_status" } };
      err.isEdgeMaskedError = true;
      return Promise.reject(err);
    }
    if (isConditionalGet(response.config)) {
      const url = response.config.url;
      if (response.status === 304) {
        const cached = etagCache.get(url);
        if (cached) {
          response.data = cached.data; // unchanged — hand back the same reference
          return response;
        }
        // Defensive: a 304 with no cached body (shouldn't happen — we only send If-None-Match
        // when a body is cached). Refetch once unconditionally rather than surface an empty body.
        etagCache.delete(url);
        return api.get(url, { headers: { "If-None-Match": undefined } });
      }
      const etag = response.headers?.etag;
      if (etag) etagCache.set(url, { etag, data: response.data });
    }
    return response;
  },
  (error) => {
    // Reached only once the retry interceptor above has given up (or declined), so a 402
    // runtime-access event still fires exactly once per real failure.
    handleRuntimeAccessError(error);
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && Array.isArray(detail.checks)) {
    const messages = detail.checks
      .map((check) => (check && typeof check.message === "string" ? check.message.trim() : ""))
      .filter(Boolean);
    if (messages.length) return [...new Set(messages)].join(" ");
  }
  if (detail && typeof detail.error === "string") return detail.error;
  if (detail && typeof detail.code === "string") {
    return detail.code.replaceAll("_", " ").replace(/^./, (character) => character.toUpperCase());
  }
  return "The request could not be completed. Please try again.";
}

export default api;
